import React from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import classNames from 'classnames'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { confirmBase, errorBase } from 'utils/ui'
import { OmTabs, OmSpinner } from '@owma/kit'

// import exclamationIcon from 'assets/images/exclamation-circle-blue-22.svg'
import styles from './EventEditor.module.scss'
// import closeIcon from 'assets/images/toolbar-close.svg'
import * as calendarService from '../../modules/CalendarService'
import AttendeeSelector from './AttendeeSelector'
import CalendarSelector from './CalendarSelector'
import DescriptionSelector from './DescriptionSelector'
import EventData from './EventData'
import LocationSelector from './LocationSelector'
import RecurrenceSelector from './RecurrenceSelector'
import ReminderSelector, { ALLDAY_REMINDER_OPTIONS, REMINDER_OPTIONS } from './ReminderSelector'
import TimeSelector from './TimeSelector'
import VisibilitySelector from './VisibilitySelector'
import AttachmentSelector from './AttachmentSelector'
import SchedulingAssistantTab from './SchedulingAssistantTab'
import AttendeeStatusList from './SchedulingAssistantTab/AttendeeStatusList'

import dataManager from 'pages/main/data/DataManager'

import exclamationIcon from 'assets/images/exclamation-circle-blue-22.svg'

import settings from 'popups/Settings'
import { getAppServices } from 'env'
import { isPersonaSession } from 'utils/personaEmail'
import { splitRecurrenceForFutureEdit } from './recurrenceSplit'

dayjs.extend(utc)
dayjs.extend(timezone)

// TypeScript interfaces
interface Attendee {
  name: string
  email: string
  role?: string
  response?: string
}

interface AttendeesProp {
  required?: Attendee[]
  optional?: Attendee[]
}

interface TargetDates {
  start: Date
  end: Date
}

interface SchedulingSelection {
  start: Date
  end: Date
  allday: boolean
}

interface EventEditorProps {
  instance?: any
  date?: Date
  targetDates?: TargetDates | null
  recurrenceEditMode?: 'this' | 'future' | 'all' | null
  duplicateEvent?: boolean
  forAllday?: boolean
  title?: string
  attendees?: AttendeesProp | null
  onClose: () => void
}

const EventEditor = ({
  instance,
  date,
  targetDates = null,
  recurrenceEditMode = null,
  duplicateEvent = false,
  forAllday = false,
  title,
  attendees = null,
  onClose,
}: EventEditorProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [inInstance, setInInstance] = useState<any>(instance || null)

  const appServices = getAppServices()
  const isCalendarOnly =
    appServices.pages.calendar === true &&
    appServices.pages.email === false &&
    appServices.pages.contacts === false &&
    appServices.can_use_email === false

  // 참석자 UI 숨김 조건: 캘린더 전용 서비스이거나 persona 세션(가면 계정)일 때
  const hideAttendees = isCalendarOnly || isPersonaSession()

  // Disable recurrence editing for 'this' and 'future' modes
  const disableRecurrence =
    (recurrenceEditMode === 'this' || recurrenceEditMode === 'future') && !duplicateEvent

  // Edit mode detection: instance exists and not duplicating
  const isEditMode = !!instance && !duplicateEvent

  // EventData 인스턴스를 한 번만 생성
  const initialEventData = useMemo(() => {
    setInInstance(instance || null)

    if (recurrenceEditMode === 'all' || recurrenceEditMode === null) {
      if (inInstance?.isRecurring) {
        return new EventData(inInstance, inInstance.event.start, 'all', targetDates)
      }
    }
    const eventData = new EventData(
      inInstance,
      date,
      recurrenceEditMode,
      targetDates,
      forAllday,
      title,
      attendees,
    )
    if (duplicateEvent) {
      eventData.uid = crypto.randomUUID()
      eventData.recurrenceid = null
      eventData.recurrenceOptions = null
      eventData.organizer = null
      eventData.orgInstance = null
      eventData.sequence = '0'
      eventData.editMode = 'all'
      eventData.isMyEvent = true
      if (eventData.calendar.isReadOnly) {
        eventData.calendar = calendarService.getDefaultCalendar()
        eventData.color = eventData.calendar.calendarColor._cdata
      }
      setInInstance(null)
    }
    return eventData
  }, [instance, date])

  const userDomain = useMemo(() => {
    return calendarService.getUserInfo().email?.split('@')[1]
  }, [])
  const [editEventData, setEditEventData] = useState<EventData>(initialEventData)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const [startDateOpen, setStartDateOpen] = useState<boolean>(false)
  const [endDateOpen, setEndDateOpen] = useState<boolean>(false)
  const [showTimezonePicker, setShowTimezonePicker] = useState(
    (editEventData.startTz !== editEventData.endTz || editEventData.startTz !== dayjs.tz.guess()) &&
      !editEventData.isAllDay,
  )
  const [showOptionalAttendees, setShowOptionalAttendees] = useState<boolean>(false)
  const [showMoreOptions, setShowMoreOptions] = useState<boolean>(!instance)
  const [activeTab, setActiveTab] = useState<string>('event')
  const [attendeeBusyTimes, setAttendeeBusyTimes] = useState<any>({})
  const [ownerHasConflict, setOwnerHasConflict] = useState<boolean>(false)

  // Scheduling selections state: empty in edit mode, populated in new mode
  const [schedulingSelections, setSchedulingSelections] = useState<SchedulingSelection[]>(() => {
    if (isEditMode) {
      return [] // Empty in edit mode - no initial selection
    } else {
      return [
        {
          // Show initial time in new mode (Date 복사로 공유 참조 mutation 방지)
          start: new Date(initialEventData.startDate),
          end: new Date(initialEventData.endDate),
          allday: initialEventData.isAllDay,
        },
      ]
    }
  })

  useEffect(() => {
    setHasChanges(inInstance ? editEventData.hasChanges(initialEventData) : true)
  }, [editEventData, initialEventData])

  useEffect(() => {
    // Optional 참석자가 있으면 Optional Selector를 표시
    if (editEventData.getOptionalAttendees().length > 0) {
      setShowOptionalAttendees(true)
    }
  }, [editEventData.attendees])

  useEffect(() => {
    // SchedulingAssistantTab으로 이동할 때 showMoreOptions를 false로 설정
    if (activeTab === 'scheduling') {
      setShowMoreOptions(false)
    }
  }, [activeTab])

  const updateEventAll = (
    vevent: any,
    willAddAttachments: any[],
    willDeleteAttachments: any[],
    completeCallback: (success: boolean) => void,
  ) => {
    let moveToCalendar = null
    if (initialEventData.getCalendar().url !== editEventData.getCalendar().url) {
      moveToCalendar = editEventData.getCalendar()
    }
    if (initialEventData.isOnlyCalendarChanged(editEventData)) {
      calendarService.moveEvent(moveToCalendar, inInstance.serverId, completeCallback)
    } else if (initialEventData.isOnlyAttachmentsChanged(editEventData)) {
      //TODO: attachments only changed
      calendarService.batchUpdateAttachments(
        inInstance.serverId,
        willAddAttachments,
        willDeleteAttachments,
        true,
        completeCallback,
      )
    } else {
      calendarService.updateEvent(
        inInstance.serverId,
        inInstance.etag,
        vevent,
        moveToCalendar,
        willAddAttachments,
        willDeleteAttachments,
        completeCallback,
      )
    }
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()

    if (!hasChanges) return

    if (
      recurrenceEditMode === 'all' &&
      inInstance &&
      initialEventData.startDate.getTime() !== editEventData.startDate.getTime()
    ) {
      confirmBase({
        icon: <img src={exclamationIcon} alt="Exclamation" />,
        title: t('common.button.warning'),
        content: t('calendar.result.changedRecurrence'),
        okText: t('common.button.ok'),
        // cancelText: t('common.button.cancel'),
        // okButtonProps: {
        //   style: { background: '#1a73e8', borderColor: '#1a73e8' },
        // },
        onOk: () => {
          inInstance.event.recurrences = null
          inInstance.event.exdate = null
          saveProcess()
        },
        onCancel: () => {},
        closable: false,
        maskClosable: false,
        keyboard: false,
        centered: true,
        className: styles.confirmModal,
        footer: (_, { OkBtn, CancelBtn }: any) => (
          <>
            <CancelBtn />
            <OkBtn />
          </>
        ),
      })
      return
    }

    saveProcess()
  }

  const saveProcess = () => {
    try {
      if (editEventData.attendees != null && editEventData.attendees.length > 0) {
        const attendees = editEventData.attendees.map((attendee: any) => {
          const ret = {
            email: attendee.email,
            name: attendee.name,
          }
          dataManager.addTempEmail(ret)
          return ret
        })

        dataManager.addRecentSendEmails(attendees)
      }

      if (inInstance && recurrenceEditMode === 'all') {
        // All 편집시 참석자가 제거되는 Exception에 있는 참석자도 제거.
        let orgAttendees = initialEventData.attendees.map((attendee: any) => {
          return attendee.email.toLowerCase()
        })
        editEventData.attendees.forEach((attendee: any) => {
          orgAttendees = orgAttendees.filter(
            (email: string) => email !== attendee.email.toLowerCase(),
          )
        })
        if (orgAttendees.length > 0 && editEventData.orgInstance?.event?.recurrences) {
          Object.entries(editEventData.orgInstance?.event?.recurrences).forEach(
            ([_, recurrence]: [string, any]) => {
              recurrence.attendee = recurrence.attendee?.filter((attendee: any) => {
                if (orgAttendees.includes(attendee.val.toLowerCase().replace('mailto:', ''))) {
                  return false
                }
                return true
              })
            },
          )
        }
      }
      // Create VEvent from EventData
      const vevent = editEventData.toVEvent()
      const willAddAttachments = editEventData.willAddAttachments()
      const willDeleteAttachments = editEventData.willDeleteAttachments

      if (!inInstance?.event.organizer && vevent.attendee.length > 0) {
        const userInfo = calendarService.getUserInfo()
        const organizer = {
          val: userInfo.email,
          params: {
            ROLE: 'ORGANIZER',
            CN: userInfo.name,
            RSVP: true,
            PARTSTAT: 'ACCEPTED',
          },
        }
        vevent.organizer = organizer
        if (inInstance) {
          if (recurrenceEditMode !== 'all') {
            inInstance.event.organizer = organizer
          }
        }
      }

      const setNeedsAction = (aVEvent: any) => {
        aVEvent.attendee?.forEach((attendee: any) => {
          attendee.params.PARTSTAT = 'NEEDS-ACTION'
        })
        aVEvent.optionalAttendees?.forEach((attendee: any) => {
          attendee.params.PARTSTAT = 'NEEDS-ACTION'
        })
      }

      if (inInstance) {
        // Update existing event
        if (recurrenceEditMode) {
          // Handle recurring event updates
          switch (recurrenceEditMode) {
            case 'this':
              const targetEvent = inInstance.event
              // 종일 마스터의 vevent.start.tz='Etc/UTC' 는 ICS 출력용 라벨일 뿐
              // recurrenceid 는 로컬 자정이다. tz 변환하면 키가 하루 앞으로 밀린다.
              const isMasterAllDay = targetEvent.datetype === 'date'
              const rKey = isMasterAllDay
                ? dayjs(vevent.recurrenceid).format('YYYY-MM-DD')
                : dayjs(vevent.recurrenceid)
                    .tz((vevent.start as any).tz)
                    .format('YYYY-MM-DD')
              setNeedsAction(vevent)
              vevent.sequence = vevent.sequence ? (parseInt(vevent.sequence) + 1).toString() : '0'
              if (targetEvent.recurrences) {
                targetEvent.recurrences[rKey] = vevent
              } else {
                targetEvent.recurrences = { [rKey]: vevent }
              }
              setIsLoading(true)
              calendarService.updateEvent(
                inInstance.serverId,
                inInstance.etag,
                targetEvent,
                null,
                willAddAttachments,
                willDeleteAttachments,
                (success: boolean) => {
                  if (success) {
                    onClose()
                  }
                  setIsLoading(false)
                },
              )
              break
            case 'future':
              const orgEvent = { ...inInstance.event }
              const splitResult = splitRecurrenceForFutureEdit({
                orgRRule: orgEvent.rrule,
                orgStart: orgEvent.start,
                newStart: vevent.start,
                newOrigOptions: vevent.rrule.origOptions as unknown as Record<string, unknown>,
              })
              orgEvent.rrule = splitResult.orgRRule
              if (splitResult.newRRule) {
                vevent.rrule = splitResult.newRRule
              }

              setNeedsAction(orgEvent)
              orgEvent.sequence = '0'

              setIsLoading(true)
              calendarService.updateEvent(
                inInstance.serverId,
                inInstance.etag,
                orgEvent,
                null,
                willAddAttachments,
                willDeleteAttachments,
                (success: boolean) => {
                  setIsLoading(false)
                  if (success) {
                    onClose()
                  }
                },
              )

              setNeedsAction(vevent)
              calendarService.createEvent(
                vevent as any,
                editEventData.getCalendar(),
                willAddAttachments,
              )
              break
            case 'all':
              setNeedsAction(vevent)
              vevent.sequence = vevent.sequence ? (parseInt(vevent.sequence) + 1).toString() : '0'
              setIsLoading(true)
              updateEventAll(
                vevent,
                willAddAttachments,
                willDeleteAttachments,
                (success: boolean) => {
                  setIsLoading(false)
                  if (success) {
                    onClose()
                  }
                },
              )
              break
            default:
              console.error('Invalid recurrence edit mode:', recurrenceEditMode)
              return
          }
        } else {
          // Update normal event
          setNeedsAction(vevent)
          vevent.sequence = vevent.sequence ? (parseInt(vevent.sequence) + 1).toString() : '0'
          setIsLoading(true)
          updateEventAll(vevent, willAddAttachments, willDeleteAttachments, (success: boolean) => {
            setIsLoading(false)
            if (success) {
              onClose()
            }
          })
        }
      } else {
        setIsLoading(true)
        calendarService.createEvent(
          vevent as any,
          editEventData.getCalendar(),
          willAddAttachments,
          (success: boolean, code?: number) => {
            setIsLoading(false)
            if (success) {
              onClose()
            } else {
              handleError('Failed to create event', code)
            }
          },
        )
      }
    } catch (error) {
      setIsLoading(false)
      handleError(error)
    }
  }

  const handleError = (error: any, code: number = 0) => {
    console.error('Failed to save event:', error)
    errorBase({
      title: t('common.button.failed'),
      content: t('editor.confirm.failedToSaveEvent') + (code > 0 ? ` (${code})` : ''),
    })
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.setTitle(e.target.value)
    setEditEventData(newEventData)
  }

  const handleDescriptionChange = (description: string) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.description = description
    setEditEventData(newEventData)
  }

  const handleCalendarChange = (calendar: any) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.setCalendar(calendar)
    newEventData.setColor(calendar.calendarColor._cdata)
    setEditEventData(newEventData)
  }

  const handleColorChange = (color: string) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.setColor(color)
    setEditEventData(newEventData)
  }

  const handleStartDateChange = (date: any) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)

    let d = dayjs(editEventData.startDate).tz(editEventData.startTz)
    d = d.set('year', date.year()).set('month', date.month()).set('date', date.date())

    const currentTime = d.toDate()
    newEventData.startDate = currentTime
    newEventData.endDate = new Date(currentTime.getTime() + newEventData.duration * 60 * 1000)

    setEditEventData(newEventData)
    setStartDateOpen(false)

    // Update scheduling selections
    setSchedulingSelections(updateSchedulingSelectionsFromEventData(newEventData))
    //TODO: 반복이 있는 경우 until 이 있으면 변경 필요?
  }

  const handleEndDateChange = (date: any) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)

    let d = dayjs(editEventData.endDate).tz(editEventData.endTz)
    d = d.set('year', date.year()).set('month', date.month()).set('date', date.date())

    const currentTime = d.toDate()
    // 종료 시간이 시작 시간보다 작으면 시작 시간 + defaultDuration으로 설정
    if (newEventData.isAllDay) {
      if (currentTime < editEventData.startDate) {
        newEventData.duration = 0
        newEventData.endDate = newEventData.startDate
      } else {
        newEventData.duration =
          (currentTime.getTime() - newEventData.startDate.getTime()) / (60 * 1000)
        newEventData.endDate = currentTime
      }
    } else {
      if (currentTime < editEventData.startDate) {
        newEventData.duration = settings.getValue('calendar').defaultDuration
        currentTime.setTime(editEventData.startDate.getTime() + newEventData.duration * 60 * 1000)
      } else {
        newEventData.duration =
          (currentTime.getTime() - newEventData.startDate.getTime()) / (60 * 1000)
      }
      newEventData.endDate = currentTime
    }

    setEditEventData(newEventData)
    setEndDateOpen(false)

    // Update scheduling selections
    setSchedulingSelections(updateSchedulingSelectionsFromEventData(newEventData))
  }

  const handleStartTimeChange = (time: string) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    let d = dayjs(editEventData.startDate).tz(editEventData.startTz)
    const [hours, minutes] = time.split(':').map(Number)

    d = d.set('hour', hours).set('minute', minutes).set('second', 0).set('millisecond', 0)

    newEventData.startDate = d.toDate()

    newEventData.endDate = new Date(
      newEventData.startDate.getTime() + newEventData.duration * 60 * 1000,
    )

    setEditEventData(newEventData)

    // Update scheduling selections
    setSchedulingSelections(updateSchedulingSelectionsFromEventData(newEventData))

    console.log(' - newEventData : ', newEventData)
  }

  const handleEndTimeChange = (time: string) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)

    let d = dayjs(editEventData.endDate).tz(editEventData.endTz)
    const [hours, minutes] = time.split(':').map(Number)

    d = d.set('hour', hours).set('minute', minutes).set('second', 0).set('millisecond', 0)

    const currentDate = d.toDate()

    // 종료 시간이 시작 시간보다 작으면 시작 시간 + defaultDuration으로 설정
    if (currentDate < editEventData.startDate) {
      newEventData.duration = settings.getValue('calendar').defaultDuration
      currentDate.setTime(editEventData.startDate.getTime() + newEventData.duration * 60 * 1000)
    } else {
      newEventData.duration =
        (currentDate.getTime() - newEventData.startDate.getTime()) / (60 * 1000)
    }
    newEventData.endDate = currentDate

    setEditEventData(newEventData)

    // Update scheduling selections
    setSchedulingSelections(updateSchedulingSelectionsFromEventData(newEventData))

    console.log(' - newEventData : ', newEventData)
  }

  const handleStartTzChange = (tz: string) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.startTz = tz
    newEventData.endTz = tz
    setEditEventData(newEventData)
  }

  const handleEndTzChange = (tz: string) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.endTz = tz
    setEditEventData(newEventData)
  }

  const handleAllDayChange = (allday: boolean) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    const isChageAllDay = newEventData.isAllDay !== allday
    newEventData.isAllDay = allday
    const calendarSettings = settings.getValue('calendar')
    if (allday) {
      newEventData.alarms =
        calendarSettings.defaultReminderTimeForAll === 1
          ? null
          : [calendarSettings.defaultReminderTimeForAll]

      const startDate = new Date(newEventData.startDate)
      startDate.setHours(0, 0, 0, 0)
      newEventData.startDate = startDate
      const endDate = new Date(newEventData.endDate)
      endDate.setHours(0, 0, 0, 0)
      newEventData.endDate = endDate
      if (isChageAllDay) {
        newEventData.isBusy = false
      }
    } else {
      newEventData.alarms =
        calendarSettings.defaultReminderTime === 1 ? null : [calendarSettings.defaultReminderTime]

      newEventData.duration = calendarSettings.defaultDuration

      const startDate = new Date(newEventData.startDate)
      const today = new Date()
      if (
        startDate.getFullYear() === today.getFullYear() &&
        startDate.getMonth() === today.getMonth() &&
        startDate.getDate() === today.getDate()
      ) {
        if (today.getMinutes() >= 30) {
          startDate.setMinutes(0)
          startDate.setHours(today.getHours() + 1, 0, 0, 0)
        } else {
          startDate.setHours(today.getHours(), 30, 0, 0)
        }
        newEventData.startDate = startDate
      } else {
        startDate.setHours(9, 0, 0, 0)
        newEventData.startDate = startDate
      }
      const endDate = new Date(newEventData.startDate.getTime() + newEventData.duration * 60 * 1000)
      newEventData.endDate = endDate
      newEventData.startTz = dayjs.tz.guess()
      newEventData.endTz = dayjs.tz.guess()
      if (isChageAllDay) {
        newEventData.isBusy = true
      }
    }
    setEditEventData(newEventData)
  }

  const handleTimezoneClick = () => {
    // if (showTimezonePicker) {
    //   confirmBase({
    //     title: 'Change to local timezone?',
    //     okText: 'Yes',
    //     cancelText: 'No',
    //     onOk: () => {
    //       const localtz = dayjs.tz.guess()
    //       const newEventData = new EventData()
    //       Object.assign(newEventData, editEventData)
    //       newEventData.startTz = localtz
    //       newEventData.endTz = localtz
    //       setEditEventData(newEventData)
    //       setShowTimezonePicker(false)
    //     },
    //   })
    // } else {
    //   setShowTimezonePicker(true)
    // };

    if (showTimezonePicker) {
      const localtz = dayjs.tz.guess()
      const newEventData = new EventData()
      Object.assign(newEventData, editEventData)
      newEventData.startTz = localtz
      newEventData.endTz = localtz
      setEditEventData(newEventData)
      setShowTimezonePicker(false)
    } else {
      setShowTimezonePicker(true)
    }
  }

  const handleLocationChange = (location: string) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.location = location
    setEditEventData(newEventData)
  }

  const handleReminderChange = (index: number, key: number) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.alarms = newEventData.alarms.slice()
    newEventData.alarms = editEventData.alarms.map((alarm: number) => alarm)
    newEventData.alarms[index] = key
    setEditEventData(newEventData)
  }

  const handleAddReminder = () => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    if (!newEventData.alarms) {
      newEventData.alarms = []
    }
    newEventData.alarms = newEventData.alarms.slice()

    const calendarSettings = settings.getValue('calendar')
    if (newEventData.isAllDay) {
      const defaultReminder = calendarSettings.defaultReminderTimeForAll
      if (defaultReminder === 1 || newEventData.alarms.includes(defaultReminder)) {
        const allDayReminderOptions = ALLDAY_REMINDER_OPTIONS().filter((option: any) => {
          return !newEventData.alarms.includes(option.key)
        })
        if (allDayReminderOptions.length > 0) {
          newEventData.alarms.push(allDayReminderOptions[0].key)
        }
      } else {
        newEventData.alarms.push(defaultReminder)
      }
    } else {
      const defaultReminder = calendarSettings.defaultReminderTime
      if (defaultReminder === 1 || newEventData.alarms.includes(defaultReminder)) {
        const reminderOptions = REMINDER_OPTIONS().filter((option: any) => {
          return !newEventData.alarms.includes(option.key)
        })
        if (reminderOptions.length > 0) {
          newEventData.alarms.push(reminderOptions[0].key)
        }
      } else {
        newEventData.alarms.push(defaultReminder)
      }
    }
    setEditEventData(newEventData)
  }

  const handleRemoveReminder = (index: number) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.alarms = newEventData.alarms.slice()
    newEventData.alarms.splice(index, 1)
    setEditEventData(newEventData)
  }

  const handleVisibilityChange = (isBusy: boolean) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.isBusy = isBusy
    setEditEventData(newEventData)
  }

  const _handlePrivateChange = (isPrivate: boolean) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.isPrivate = isPrivate
    setEditEventData(newEventData)
  }

  const handleAttendeesChange = (attendees: Attendee[]) => {
    setEditEventData((prev) => {
      const newEventData = new EventData()
      Object.assign(newEventData, prev)
      newEventData.attendees = attendees.map(
        (attendee) => ({ ...attendee, role: 'REQ-PARTICIPANT' }) as (typeof prev.attendees)[number],
      )
      return newEventData
    })
  }

  const handleOptionalAttendeesChange = (attendees: Attendee[]) => {
    setEditEventData((prev) => {
      const newEventData = new EventData()
      Object.assign(newEventData, prev)
      newEventData.optionalAttendees = attendees.map(
        (attendee) =>
          ({ ...attendee, role: 'OPT-PARTICIPANT' }) as (typeof prev.optionalAttendees)[number],
      )
      return newEventData
    })
  }

  const handleOptionalClick = () => {
    setShowOptionalAttendees(true)
  }

  const handleRecurrenceChange = (rule: any) => {
    // Prevent changes if recurrence is disabled
    if (disableRecurrence) return

    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    if (rule && rule.freq >= 0) {
      newEventData.setRecurrenceOptions(rule)
    } else {
      newEventData.setRecurrenceOptions(null)
    }
    setEditEventData(newEventData)
  }

  const handleClose = () => {
    if (!hasChanges) {
      onClose()
      return
    }

    if (inInstance === null && !editEventData.hasChanges(initialEventData)) {
      onClose()
      return
    }

    confirmBase({
      icon: <img src={exclamationIcon} alt="Exclamation" />,
      title: t('calendar.result.changesTitle'),
      content: t('calendar.result.changesContent'),
      okText: t('common.button.ok'),
      cancelText: t('common.button.cancel'),
      closeIcon: false,
      onOk: () => {
        onClose()
      },
      onCancel: () => {},
      closable: false,
      maskClosable: false,
      keyboard: false,
      centered: true,
      // className: styles.confirmModal,
      // footer: (_, { OkBtn, CancelBtn }) => (
      //   <>
      //     <CancelBtn />
      //     <OkBtn />
      //   </>
      // ),
    })
  }

  const handleAddAttachment = (file: any) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.addAttachment(file)
    setEditEventData(newEventData)
  }

  const handleDeleteAttachment = (attachment: any) => {
    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)
    newEventData.deleteAttachment(attachment)
    setEditEventData(newEventData)
  }

  /**
   * All day 이벤트를 시간 설정 이벤트로 변환
   *
   * All day → 시간 설정 변환 시:
   * - isAllDay = false
   * - timezone 설정 (dayjs.tz.guess())
   * - reminder 변경 (all day용 → 일반 시간용)
   *
   * @param {EventData} newEventData - 업데이트할 EventData 객체
   * @param {Date} startDate - 선택된 시작 시간
   * @param {Date} endDate - 선택된 종료 시간
   */
  const convertAllDayToTimedEvent = (newEventData: EventData, startDate: Date, endDate: Date) => {
    const calendarSettings = settings.getValue('calendar')

    // All day → 시간 설정 변환
    newEventData.isAllDay = false

    // Timezone 설정 (All day에는 timezone이 없으므로)
    newEventData.startTz = dayjs.tz.guess()
    newEventData.endTz = dayjs.tz.guess()

    // Reminder 변경 (All day용 → 일반 시간용)
    newEventData.alarms =
      calendarSettings.defaultReminderTime === 1 ? null : [calendarSettings.defaultReminderTime]

    // 시간 및 duration 설정
    newEventData.startDate = startDate
    newEventData.endDate = endDate
    newEventData.duration = (endDate.getTime() - startDate.getTime()) / (60 * 1000)
  }

  /**
   * Helper: Update scheduling selections based on event data
   *
   * @param {EventData} eventData - Event data to extract time from
   * @returns {Array} Scheduling selections array
   */
  const updateSchedulingSelectionsFromEventData = useCallback(
    (eventData: EventData): SchedulingSelection[] => {
      return [
        {
          start: new Date(eventData.startDate),
          end: new Date(eventData.endDate),
          allday: eventData.isAllDay,
        },
      ]
    },
    [],
  )

  /**
   * SchedulingAssistant에서 시간 선택 시 호출되는 핸들러
   *
   * Selections 배열의 첫 번째 항목을 사용하여 이벤트 시간을 업데이트
   * All day 이벤트를 편집 중일 때 시간 설정으로 선택하면 자동으로 변환
   *
   * @param {Array<{start: Date, end: Date, allday: boolean}>} selections - 선택된 시간 범위 배열
   */
  const handleSchedulingSelectionsChange = (selections: SchedulingSelection[]) => {
    // selections 배열이 비어있거나 없으면 아무것도 하지 않음
    if (!selections || selections.length === 0) {
      return
    }

    // 첫 번째 selection 사용 (single-mode)
    const { start: startDate, end: endDate, allday: selectedIsAllDay } = selections[0]

    const newEventData = new EventData()
    Object.assign(newEventData, editEventData)

    // 현재 All Day 이벤트이고, 선택된 시간이 시간 설정이면 변환
    if (editEventData.isAllDay && !selectedIsAllDay) {
      convertAllDayToTimedEvent(newEventData, startDate, endDate)
    } else if (!editEventData.isAllDay && selectedIsAllDay) {
      // Timed → All Day 변환
      const calendarSettings = settings.getValue('calendar')
      newEventData.isAllDay = true
      newEventData.alarms =
        calendarSettings.defaultReminderTimeForAll === 1
          ? null
          : [calendarSettings.defaultReminderTimeForAll]

      // All day 시간으로 설정 (00:00:00)
      const allDayStart = new Date(startDate)
      allDayStart.setHours(0, 0, 0, 0)
      const allDayEnd = new Date(endDate)
      allDayEnd.setHours(0, 0, 0, 0)

      newEventData.startDate = allDayStart
      newEventData.endDate = allDayEnd
      newEventData.duration = (allDayEnd.getTime() - allDayStart.getTime()) / (60 * 1000)
    } else {
      // 일반적인 시간 업데이트 (변환 없음)
      newEventData.startDate = startDate
      newEventData.endDate = endDate
      newEventData.duration = (endDate.getTime() - startDate.getTime()) / (60 * 1000)

      // allday 상태가 변경되었으면 업데이트
      if (newEventData.isAllDay !== selectedIsAllDay) {
        newEventData.isAllDay = selectedIsAllDay
      }
    }

    setEditEventData(newEventData)

    // Update scheduling selections state (persists across tab switches)
    setSchedulingSelections(selections)
  }

  // startDate 값이 실제로 변경될 때만 새 Date 객체 생성
  // (매 렌더마다 new Date()를 전달하면 SchedulingAssistantTab의 browseDate가 원복됨)
  const startDateTimestamp = editEventData.startDate?.getTime()
  const schedulingCurrentDate = useMemo(
    () => new Date(editEventData.startDate),
    [startDateTimestamp],
  )

  // Compute attendees list with organizer included
  const attendeesWithOrganizer = useMemo(() => {
    const attendees = [...editEventData.attendees]
    // Add organizer at the beginning if present
    //if (editEventData.organizer) {
    if (editEventData.calendar?.owner) {
      const owner = calendarService.getUserInfo()
      if (editEventData.calendar.owner === owner.email) {
        attendees.unshift({
          email: owner.email,
          name: owner.name,
          role: 'ORGANIZER',
        } as any)
      } else {
        attendees.unshift({
          email: editEventData.calendar.owner,
          name: editEventData.calendar.owner.split('@')[0],
          role: 'ORGANIZER',
        } as any)
      }
    }
    return attendees
  }, [editEventData.attendees, editEventData.calendar?.owner])

  // Tab items for Event Editor
  const tabItems = [
    {
      key: 'event',
      label: <span>{t('calendar.schedulingAssistant.eventTab')}</span>,
      children: (
        <div className={styles.content}>
          <div
            className={`${styles.titleInputWrapper} ${!editEventData.isMyEvent ? styles.dimmed : ''}`}
          >
            <div className={styles.titleIcon} />
            <input
              type="text"
              className={styles.titleInput}
              placeholder={t('calendar.edit.addTitle')}
              value={editEventData.getTitle()}
              onChange={handleTitleChange}
              autoFocus
            />
          </div>
          <CalendarSelector
            editEventData={editEventData as any}
            calendars={calendarService.getCalendars() as any}
            onCalendarChange={handleCalendarChange}
            onColorChange={handleColorChange}
            disabled={disableRecurrence}
          />
          <TimeSelector
            editEventData={editEventData}
            startDateOpen={startDateOpen}
            endDateOpen={endDateOpen}
            showTimezonePicker={showTimezonePicker}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            onStartTimeChange={handleStartTimeChange}
            onEndTimeChange={handleEndTimeChange}
            onAllDayChange={handleAllDayChange}
            onTimezoneClick={handleTimezoneClick}
            setStartDateOpen={setStartDateOpen}
            setEndDateOpen={setEndDateOpen}
            onStartTzChange={handleStartTzChange}
            onEndTzChange={handleEndTzChange}
          />
          {!showMoreOptions && (
            <RecurrenceSelector
              editEventData={editEventData}
              onRecurrenceChange={handleRecurrenceChange}
              disabled={disableRecurrence}
            />
          )}
          {!hideAttendees && (
            <AttendeeSelector
              editEventData={editEventData}
              onAttendeeChange={handleAttendeesChange as any}
              isRequired={true}
              hideOptionalBtn={showOptionalAttendees}
              onOptionalClick={handleOptionalClick}
              userDomain={userDomain}
            />
          )}
          {!hideAttendees && showOptionalAttendees && (
            <AttendeeSelector
              editEventData={editEventData}
              onAttendeeChange={handleOptionalAttendeesChange as any}
              isRequired={false}
              userDomain={userDomain}
            />
          )}
          <LocationSelector editEventData={editEventData} onLocationChange={handleLocationChange} />
          {!showMoreOptions && (
            <ReminderSelector
              editEventData={editEventData}
              onReminderChange={handleReminderChange}
              onAddReminder={handleAddReminder}
              onRemoveReminder={handleRemoveReminder}
              isAllDay={editEventData.isAllDay}
            />
          )}
          {!showMoreOptions && (
            <VisibilitySelector
              editEventData={editEventData}
              onVisibilityChange={handleVisibilityChange}
            />
          )}
          {!showMoreOptions && !__ENV_PRODUCTION__ && (
            <AttachmentSelector
              editEventData={editEventData}
              onAddAttachment={handleAddAttachment}
              onDeleteAttachment={handleDeleteAttachment}
            />
          )}
          <DescriptionSelector
            editEventData={editEventData}
            onDescriptionChange={handleDescriptionChange}
          />
        </div>
      ),
    },
    {
      key: 'scheduling',
      label: <span>{t('calendar.schedulingAssistant.schedulingTab')}</span>,
      disabled: !editEventData.isMyEvent,
      children: (
        <SchedulingAssistantTab
          attendees={attendeesWithOrganizer}
          currentDate={schedulingCurrentDate}
          initialSelections={schedulingSelections}
          onSelectionsChange={handleSchedulingSelectionsChange}
          activeTab={activeTab}
          eventTitle={editEventData.getTitle()}
          onAttendeeBusyTimesChange={setAttendeeBusyTimes}
          onOwnerConflictChange={setOwnerHasConflict}
        />
      ),
    },
  ].filter((item) => !(hideAttendees && item.key === 'scheduling'))

  return (
    <div className={styles.container} data-testid="calendar-event-editor">
      <OmSpinner spinning={isLoading}>
        <OmTabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className={classNames(styles.editorTabs, 'custom-tabs-nav')}
          aria-label={t('calendar.schedulingAssistant.tabsLabel')}
        />
        <div className={styles.availabilityPickerFooterContent}>
          {!hideAttendees && activeTab === 'scheduling' && attendeesWithOrganizer.length > 0 && (
            <AttendeeStatusList
              attendees={attendeesWithOrganizer}
              busyTimes={attendeeBusyTimes}
              ownerHasConflict={ownerHasConflict}
              selectedTimeRange={
                schedulingSelections.length > 0 && schedulingSelections[0]
                  ? {
                      start: schedulingSelections[0].start,
                      end: schedulingSelections[0].end,
                    }
                  : null
              }
              maxVisible={5}
            />
          )}
          <div className={styles.footer}>
            {activeTab !== 'scheduling' && showMoreOptions ? (
              <button className={styles.moreOptions} onClick={() => setShowMoreOptions(false)}>
                {t('calendar.edit.moreOptions')}
              </button>
            ) : (
              <div>&nbsp;</div>
            )}

            <div className={styles.right}>
              <button
                className={styles.cancelButton}
                onClick={handleClose}
                style={{ minWidth: 100 }}
              >
                {t('common.button.cancel')}
              </button>
              <button
                className={`${styles.saveButton} ${!hasChanges ? styles.disabled : ''}`}
                onClick={handleSave}
                disabled={!hasChanges}
              >
                {editEventData.attendees.length > 0
                  ? t('common.button.send')
                  : t('common.button.save')}
              </button>
            </div>
          </div>
        </div>
      </OmSpinner>
    </div>
  )
}

export default EventEditor
